import React, { useEffect, useState } from 'react';
import { View, StyleSheet, LayoutChangeEvent, Dimensions, ViewProps } from 'react-native';
import {
  TapGestureHandler,
  PanGestureHandler,
  State,
  TapGestureHandlerStateChangeEvent,
} from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  useAnimatedGestureHandler,
  withTiming,
  interpolate,
} from 'react-native-reanimated';
import { Platform } from 'src/libs/platform';
import { ANDROID_EASING, IOS_EASING } from 'src/themes/constants';

export type SlideDownHandlerProps = ViewProps & {
  onRequestClose: () => void;
  visible: boolean;
  onEnd?: () => void;
};

const SCREEN_HEIGHT = Dimensions.get('screen').height;
const DURATION = Platform.OS === 'ios' ? 400 : 320;
const EASING = Platform.OS === 'ios' ? IOS_EASING : ANDROID_EASING;
const CLOSE_THRESHOLD = 100;

export const SlideDownHandler: React.FC<SlideDownHandlerProps> = ({
  children,
  onRequestClose,
  visible,
  onEnd,
  style,
}) => {
  const [shouldRender, toggle] = useState(false);
  const sv = useSharedValue(SCREEN_HEIGHT);
  const height = useSharedValue(SCREEN_HEIGHT);

  const onAnimationEnd = () => {
    if (onEnd) onEnd();
  };

  useEffect(() => {
    if (visible && !shouldRender) {
      toggle(true);
      sv.value = withTiming(0, {
        duration: DURATION,
        easing: EASING,
      });
    }

    if (!visible && shouldRender) {
      sv.value = withTiming(
        Math.min(SCREEN_HEIGHT, height.value),
        { duration: DURATION, easing: EASING },
        () => runOnJS(onAnimationEnd)()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shouldRender]);

  const gestureHandler = useAnimatedGestureHandler({
    onStart: () => {
      sv.value = 0;
    },
    onActive: e => {
      if (e.translationY > 0) {
        sv.value = e.translationY;
      }
    },
    onEnd: e => {
      if (e.velocityY > 3 || e.translationY > CLOSE_THRESHOLD) {
        runOnJS(onRequestClose)();
        sv.value = withTiming(SCREEN_HEIGHT);
      } else {
        sv.value = withTiming(0);
      }
    },
  });

  const tapGestureHandler = ({ nativeEvent: e }: TapGestureHandlerStateChangeEvent) => {
    if (e.state === State.ACTIVE) {
      runOnJS(onRequestClose)();
    }
  };

  const updateHeight = (e: LayoutChangeEvent) => {
    height.value = withTiming(e.nativeEvent.layout.height);
  };

  const transition = useAnimatedStyle(() => ({
    transform: [{ translateY: sv.value }],
  }));

  const backdropTransition = useAnimatedStyle(() => ({
    opacity: interpolate(sv.value, [0, height.value], [1, 0]),
  }));

  return (
    <PanGestureHandler onGestureEvent={gestureHandler}>
      <View style={[s.root]}>
        <TapGestureHandler onHandlerStateChange={tapGestureHandler}>
          <Animated.View style={[s.backdrop, backdropTransition]} />
        </TapGestureHandler>
        <Animated.View style={[style, transition]} onLayout={updateHeight}>
          {children}
        </Animated.View>
      </View>
    </PanGestureHandler>
  );
};

const s = StyleSheet.create({
  root: {
    height: '100%',
    justifyContent: 'flex-end',
    ...StyleSheet.absoluteFillObject,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, .4)',
    ...StyleSheet.absoluteFillObject,
  },
});
